import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type SwiperCore from 'swiper';

import {
  type IconButtonProps,
  Box,
  Flex,
  Heading,
  IconButton,
  Text,
  useBreakpointValue,
  useToken,
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@udacity/chakra-uds-icons';

import { ContentContainer } from '~/components/container';
import { VideoModal } from '~/components/video-modal';
import { trackEvent } from '~/features/analytics/track-event';
import { ChevronLeftIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useExperiment } from '~/features/experiments/hooks/use-experiment';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import {
  EXPERIENCE_ATF_CARDS,
  EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX,
  EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE,
  EXPERIENCE_ATF_FLASH_PROMO_CARD,
  EXPERIENCE_ATF_PROMOTION_ID,
  ExperienceAtfCardConfig,
  FREE_TRIAL_ATF_CARD,
} from '../constants';
import { useExperienceAtfCarouselStartInsetPx } from '../hooks/use-experience-atf-carousel-start-inset-px';
import { formatAtfPromoEventLabel } from '../utils/format-atf-promo-event-label';

import { ExperienceAtfHeroCarouselTrack } from './experience-atf-hero-carousel-track';

/** Swiper 9 runtime API; omitted from published `.d.ts` but required for edge detection vs `translate`. */
type SwiperWithTranslateBounds = SwiperCore & {
  maxTranslate(): number;
  minTranslate(): number;
};

/** Figma node 6576:21673 — carousel prev/next control (default + hover/active). */
const experienceAtfCarouselNavButtonStyles: Pick<
  IconButtonProps,
  | 'alignItems'
  | 'backdropFilter'
  | 'bg'
  | 'borderRadius'
  | 'color'
  | 'display'
  | 'height'
  | 'justifyContent'
  | 'minWidth'
  | 'sx'
  | 'variant'
  | 'width'
  | '_active'
  | '_disabled'
  | '_focusVisible'
  | '_hover'
> = {
  _active: { bg: 'blue.500', color: 'white' },
  _disabled: { cursor: 'not-allowed', opacity: 0.5 },
  _focusVisible: { boxShadow: '0 0 0 3px rgba(23, 92, 255, 0.45)' },
  _hover: { bg: 'blue.500', color: 'white' },
  alignItems: 'center',
  backdropFilter: 'blur(3px)',
  bg: 'rgba(156, 159, 168, 0.4)',
  borderRadius: '40px',
  color: 'gray.700',
  display: { base: 'none', md: 'inline-flex' },
  height: '56px',
  justifyContent: 'center',
  minWidth: '56px',
  sx: {
    '&:disabled:hover': {
      bg: 'rgba(156, 159, 168, 0.4)',
      color: 'gray.700',
    },
  },
  variant: 'unstyled',
  width: '56px',
};

export type ExperienceAtfHeroSectionProps = StaticExperienceAtfHeroSectionProps;

/**
 * Hero for /experience ATF (promoted from ab_experience_atf_2026 variant_b).
 * Layout + promo cards per Figma node 7374:9689 (gradient hero, promo row, overlap into content below).
 */
export function ExperienceAtfHeroSection({ title, description }: ExperienceAtfHeroSectionProps) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [isVisuallyAtBeginning, setIsVisuallyAtBeginning] = useState(true);
  const [isVisuallyAtEnd, setIsVisuallyAtEnd] = useState(false);
  const viewedCardIdsRef = useRef<Set<string>>(new Set());
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperCore | null>(null);
  const isClientReady = useClientRender();
  const { data: promotionData, isFetched: promotionFetched } = usePromotion();
  const isMobileFromBreakpoint = useBreakpointValue({ base: true, xl: false }, { fallback: 'xl' });
  /**
   * Swiper slide widths / offsets still need a boolean after mount. Promo cards avoid
   * `useBreakpointValue` for dimensions and use responsive Chakra props instead so SSR + hydration
   * agree per viewport (no “mobile first, desktop after useEffect” CLS).
   */
  const isMobile = isMobileFromBreakpoint ?? true;

  const showFlashPromo = promotionData != null && promotionData.promotionId === EXPERIENCE_ATF_PROMOTION_ID;
  const experiment = useExperiment();
  const isFreeTrialOn = experiment?.['agentic-ai-ft-2026'] === 'on';
  const displayCards = useMemo<ExperienceAtfCardConfig[]>(() => {
    const base = isFreeTrialOn
      ? [FREE_TRIAL_ATF_CARD, ...EXPERIENCE_ATF_CARDS.filter((c) => c.id !== 'agentic_ai_ns')]
      : EXPERIENCE_ATF_CARDS;
    return showFlashPromo ? [EXPERIENCE_ATF_FLASH_PROMO_CARD, ...base] : base;
  }, [showFlashPromo, isFreeTrialOn]);
  const carouselReady = isClientReady && promotionFetched;

  const [bodyContentMaxWidth] = useToken('sizes', ['content-max-width-lg']);
  const slidesStartInsetPx = useExperienceAtfCarouselStartInsetPx(bodyContentMaxWidth);
  const { i18n, isRtl } = useI18n();

  const handleCardClick = useCallback(
    (card: ExperienceAtfCardConfig) => {
      trackEvent({
        type: 'click',
        name: 'ExperiencePage CTA Clicked',
        label: formatAtfPromoEventLabel(card.promoVariant),
        additionalAttributes: {
          card_position: displayCards.indexOf(card) + 1,
        },
      });
    },
    [displayCards]
  );

  const openVideo = useCallback(
    (card: ExperienceAtfCardConfig) => {
      if (card.action.type === 'video') {
        handleCardClick(card);
        setActiveVideoId(card.action.videoId);
      }
    },
    [handleCardClick]
  );

  const syncVisualNavEdges = useCallback((swiper: SwiperCore) => {
    const EDGE_EPSILON_PX = 2;
    const s = swiper as SwiperWithTranslateBounds;
    const minTranslate = s.minTranslate();
    const maxTranslate = s.maxTranslate();
    const translate = swiper.translate;

    const atBeginning = swiper.isLocked || swiper.isBeginning || Math.abs(translate - minTranslate) <= EDGE_EPSILON_PX;
    const atEnd = swiper.isLocked || swiper.isEnd || Math.abs(maxTranslate - translate) <= EDGE_EPSILON_PX;

    setIsVisuallyAtBeginning(atBeginning);
    setIsVisuallyAtEnd(atEnd);
  }, []);

  const handleBeforeInit = useCallback(
    (swiper: SwiperCore) => {
      swiper.changeLanguageDirection(i18n.dir());
      const nav = swiper.params.navigation;
      if (nav && typeof nav === 'object') {
        nav.prevEl = prevRef.current;
        nav.nextEl = nextRef.current;
      }
    },
    [i18n]
  );

  const handleDestroy = useCallback(() => {
    swiperRef.current = null;
  }, []);

  const handleSwiper = useCallback(
    (swiper: SwiperCore) => {
      swiperRef.current = swiper;
      swiper.navigation?.update?.();
      syncVisualNavEdges(swiper);
    },
    [syncVisualNavEdges]
  );

  const navDisabled = !carouselReady || displayCards.length < 2;
  const prevNavIcon = isRtl ? (
    <ChevronRightIcon height='22px' width='22px' />
  ) : (
    <ChevronLeftIcon height='22px' width='22px' />
  );
  const nextNavIcon = isRtl ? (
    <ChevronLeftIcon height='22px' width='22px' />
  ) : (
    <ChevronRightIcon height='22px' width='22px' />
  );

  /**
   * Swiper sets `disabled` + `swiper-button-disabled` on custom nav elements. Chakra `IconButton`
   * with `isDisabled={false}` reapplies `disabled={false}` on every React render, wiping Swiper’s
   * state until the next Swiper event. Re-sync navigation after each paint when the carousel is live.
   */
  useLayoutEffect(() => {
    if (navDisabled) {
      return;
    }
    swiperRef.current?.navigation?.update?.();
  });

  return (
    <Box as='section' position='relative'>
      <Flex
        bgGradient='linear(to-b, black 0%, #011d62 100%)'
        flexDirection='column'
        paddingBottom={{ base: '60px', md: '80px' }}
        position='relative'
        minHeight={{
          base: '248px',
          md: '350px',
        }}
      >
        <ContentContainer contentMaxWidth='content-max-width-xl'>
          <Flex
            alignItems='center'
            flexDirection='column'
            gap={{ base: 4, md: 6 }}
            marginX='auto'
            maxWidth='1240px'
            textAlign='center'
          >
            <Heading
              as='h1'
              color='white'
              fontSize={{ base: '2.5rem', md: '3.5rem', xl: '4.75rem' }}
              fontWeight='500'
              letterSpacing='-0.02em'
              lineHeight={1}
            >
              {title}
            </Heading>
            <Text color='gray.100' fontSize={{ base: '1rem', md: 'lg' }} lineHeight={1.5} maxWidth='822px'>
              {description}
            </Text>
          </Flex>
        </ContentContainer>
      </Flex>

      <Box
        overflow='visible'
        paddingBottom={{ base: 4, md: 6 }}
        position='relative'
        width='100%'
        marginTop={{
          base: '-60px',
          md: '-80px',
        }}
      >
        <Box margin='0 auto' maxWidth='100%' position='relative' width='100%'>
          {/*
            DOM order = paint order for positioned siblings (no z-index): track first, then prev, then
            next so arrows sit above slides without stacking hacks.
          */}
          <Box
            isolation='isolate'
            opacity={carouselReady ? 1 : 0}
            overflowX='hidden'
            width='100%'
            minHeight={{
              base: `${EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX_MOBILE}px`,
              xl: `${EXPERIENCE_ATF_CAROUSEL_MIN_HEIGHT_PX}px`,
            }}
            sx={
              carouselReady
                ? {
                    '.swiper .swiper-slide': {
                      display: 'flex',
                      height: 'auto',
                    },
                    '.swiper': {
                      overflow: 'hidden',
                    },
                  }
                : undefined
            }
          >
            <ExperienceAtfHeroCarouselTrack
              bodyContentMaxWidth={bodyContentMaxWidth}
              cards={displayCards}
              isClientReady={carouselReady}
              isMobile={isMobile}
              nextRef={nextRef}
              prevRef={prevRef}
              slidesStartInsetPx={slidesStartInsetPx}
              viewedCardIdsRef={viewedCardIdsRef}
              onBeforeInit={handleBeforeInit}
              onCardClick={handleCardClick}
              onDestroy={handleDestroy}
              onResize={syncVisualNavEdges}
              onSlideChange={syncVisualNavEdges}
              onSwiper={handleSwiper}
              onVideo={openVideo}
            />
          </Box>
          <Box
            bottom={0}
            insetInlineEnd={0}
            insetInlineStart={0}
            marginInline='auto'
            maxWidth='1920px'
            pointerEvents='none'
            position='absolute'
            top={0}
            width='100%'
          >
            {(
              [
                {
                  ref: prevRef,
                  'aria-label': 'Previous promo',
                  'data-experience-atf-carousel-nav': 'prev' as const,
                  display: navDisabled || isVisuallyAtBeginning ? 'none' : { base: 'none', md: 'inline-flex' },
                  icon: prevNavIcon,
                  insetInlineStart: '16px',
                  paddingInlineEnd: '2px',
                },
                {
                  ref: nextRef,
                  'aria-label': 'Next promo',
                  'data-experience-atf-carousel-nav': 'next' as const,
                  display: navDisabled || isVisuallyAtEnd ? 'none' : { base: 'none', md: 'inline-flex' },
                  icon: nextNavIcon,
                  insetInlineEnd: { md: 4, xl: 8 },
                  paddingInlineStart: '2px',
                },
              ] as const
            ).map((navProps) => (
              <IconButton
                key={navProps['data-experience-atf-carousel-nav']}
                {...experienceAtfCarouselNavButtonStyles}
                {...navProps}
                aria-hidden={navDisabled}
                isDisabled={navDisabled}
                pointerEvents='auto'
                position='absolute'
                top='50%'
                transform='translateY(-50%)'
                transition='opacity 0.25s ease, background 0.15s ease, color 0.15s ease'
                sx={{
                  ...experienceAtfCarouselNavButtonStyles.sx,
                  '&.swiper-button-disabled': {
                    display: 'none !important',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <VideoModal title='Webinar Q&A' videoId={activeVideoId} onClose={() => setActiveVideoId(null)} />
    </Box>
  );
}

export function transformStaticExperienceAtfHeroSectionProps() {
  return {
    title: `Learning built for careers`,
    description: `Build career-ready skills with projects, mentors, and courses created with top tech leaders.`,
  };
}

export type StaticExperienceAtfHeroSectionProps = ReturnType<typeof transformStaticExperienceAtfHeroSectionProps>;
